import { useEffect, useMemo, useState } from 'react';
import Stack from '@mui/material/Stack';
import Checkbox from '@mui/material/Checkbox';
import Typography from '@mui/material/Typography';
import {
  constants,
  makeHeader,
  makeRow,
  notify,
  BaseDialog,
  Loader,
  SecondaryButton,
  StandardTable,
} from '@bento-dental/core';
import { DatePickerField } from '@bento-dental/core/fields';
import { useFormState } from '@bento-dental/core/hooks';
import { getRelationshipLabel } from '@bento-dental/core/members';
import { formatDate, isValidDate } from '@bento-dental/core/utilities';
import { useCobra } from './EmployeeUtils';
import { CobraFamilyMember, CoverageMember, EmployeeCoverage } from './types';

interface CobraCancelDialogProps {
  open: boolean;
  onClose: () => void;
  refresh: () => void;
  patientId?: string | number | null;
  member?: Partial<CoverageMember> | EmployeeCoverage | null;
}

interface FormState {
  cancelCobraIds: string[];
  finalDateOfCoverage: string | null;
  cancelCobraIdsError: string;
  cobraFinalDateOfCoverageError: string;
}

const initialFormState: FormState = {
  cancelCobraIds: [],
  finalDateOfCoverage: null,
  cancelCobraIdsError: '',
  cobraFinalDateOfCoverageError: '',
};

function CobraCancelDialog({
  open,
  onClose: onCloseFn,
  refresh,
  patientId,
  member,
}: CobraCancelDialogProps) {
  const { formState, setFormState } = useFormState(initialFormState) as {
    formState: FormState;
    setFormState: (updates: Partial<FormState>) => void;
  };
  const [showLoader, setShowLoader] = useState(false);
  const [cobraMembers, setCobraMembers] = useState<CobraFamilyMember[] | null>(null);
  const { cobraElectionInfo, cobraBulkCancel } = useCobra();

  useEffect(() => {
    if (member?.cobra_id) {
      setFormState({ cancelCobraIds: [String(member.cobra_id)] });
    }
  }, [member, setFormState]);

  useEffect(() => {
    if (open && patientId && cobraElectionInfo) {
      setShowLoader(true);
      cobraElectionInfo
        .mutateAsync({ patientId: String(patientId) })
        .then((response) => {
          setCobraMembers(response.data.data.family_members ?? null);
        })
        .catch((error) => {
          const message = error instanceof Error ? error.message : 'Unable to load COBRA info.';
          notify(message, 'error');
        })
        .finally(() => {
          setShowLoader(false);
        });
    }
  }, [open, patientId]);

  const onClose = () => {
    setFormState(initialFormState);
    onCloseFn();
  };

  const bulkCancel = () => {
    const { finalDateOfCoverage } = formState;
    if (!finalDateOfCoverage) {
      return;
    }
    setShowLoader(true);
    cobraBulkCancel
      .mutateAsync({
        finalDateOfCoverage: formatDate(finalDateOfCoverage, 'YYYY-MM-DD'),
        cancelCobraIds: formState.cancelCobraIds,
      })
      .then(() => {
        notify('Successfully canceled COBRA for selected family members.', 'success');
        refresh();
        onClose();
      })
      .catch((error) => {
        const message = error instanceof Error ? error.message : 'Unable to cancel COBRA.';
        notify(message, 'error');
      })
      .finally(() => {
        setShowLoader(false);
      });
  };

  const canSubmit = (submit: () => void) => {
    const errors: Partial<FormState> = {};
    if (!formState.finalDateOfCoverage) {
      errors.cobraFinalDateOfCoverageError = constants.VALIDATION.REQUIRED;
    } else if (!isValidDate(formState.finalDateOfCoverage)) {
      errors.cobraFinalDateOfCoverageError = constants.VALIDATION.INVALID;
    }
    if (!formState.cancelCobraIds.length) {
      errors.cancelCobraIdsError = constants.VALIDATION.REQUIRED;
    }
    if (Object.keys(errors).length > 0) {
      setFormState(errors);
    } else {
      submit();
    }
  };

  const headers = makeHeader('covered-family', ['Cancel', 'Name', 'Relationship', 'Status']);

  const rows = (cobraMembers ?? []).map((familyMember) =>
    makeRow(`covered-family-${familyMember.id}`, [
      <Checkbox
        id={`covered-family-checkbox-${familyMember.patient}`}
        key={`covered-family-checkbox-${familyMember.patient}`}
        color="secondary"
        checked={
          Boolean(familyMember.has_cobra) &&
          formState.cancelCobraIds.includes(String(familyMember.cobra_id))
        }
        value={String(familyMember.cobra_id)}
        onChange={(event) => {
          const { cancelCobraIds } = formState;
          const cobraId = String(event.target.value);
          const updatedIds = cancelCobraIds.includes(cobraId)
            ? cancelCobraIds.filter((id) => id !== cobraId)
            : [...cancelCobraIds, cobraId];
          setFormState({ cancelCobraIds: updatedIds, cancelCobraIdsError: '' });
        }}
        disabled={!familyMember.has_cobra}
      />,
      familyMember.name,
      getRelationshipLabel(familyMember.relation),
      familyMember.cobra_cancel_status,
    ])
  );

  const dialogActions = {
    'Cancel COBRA': () => {
      canSubmit(bulkCancel);
    },
    Dismiss: () => {
      onClose();
    },
  };

  const minCancellationDate = useMemo(() => {
    if (!cobraMembers) return null;
    const dates = cobraMembers
      .filter((member) => member.cobra_coverage_start_date)
      .map((member) => {
        const date = member.cobra_coverage_start_date!;
        return parseInt(
          `${date.substring(6, 10)}${date.substring(0, 2)}${date.substring(3, 5)}`,
          10
        );
      });
    if (!dates.length) return null;
    const date = Math.min(...dates)
      .toString()
      .padStart(8, '0');
    return `${date.substring(4, 6)}/${date.substring(6, 8)}/${date.substring(0, 4)}`;
  }, [cobraMembers]);

  const maxCancellationDate = useMemo(() => {
    if (!cobraMembers) return null;
    const dates = cobraMembers
      .filter((member) => member.cobra_coverage_end_date)
      .map((member) => {
        const date = member.cobra_coverage_end_date!;
        return parseInt(
          `${date.substring(6, 10)}${date.substring(0, 2)}${date.substring(3, 5)}`,
          10
        );
      });
    if (!dates.length) return null;
    const date = Math.max(...dates)
      .toString()
      .padStart(8, '0');
    return `${date.substring(4, 6)}/${date.substring(6, 8)}/${date.substring(0, 4)}`;
  }, [cobraMembers]);

  return (
    <BaseDialog
      open={open}
      onClose={onClose}
      dialogName="cancel-cobra-dialog"
      fullWidth={false}
      maxWidth="md"
      title="Cancel Cobra"
      actionButtonComponent={SecondaryButton}
      actions={dialogActions}
    >
      {showLoader ? (
        <Loader size="sm" />
      ) : (
        <Stack>
          <StandardTable id="covered-family" headers={headers} rows={rows} />
          {formState.cancelCobraIdsError && (
            <Typography fontSize={12} marginLeft={1.8} marginTop={-1.5} color="error">
              Select at least one member
            </Typography>
          )}
          <DatePickerField
            id="cobra-final-date-of-coverage"
            name="finalDateOfCoverage"
            label="Final date of coverage"
            value={formState.finalDateOfCoverage}
            onChange={(event) => {
              setFormState({
                finalDateOfCoverage: event.target.value,
                cobraFinalDateOfCoverageError: '',
              });
            }}
            minDate={minCancellationDate}
            maxDate={maxCancellationDate}
            error={Boolean(formState.cobraFinalDateOfCoverageError)}
            helperText={formState.cobraFinalDateOfCoverageError}
            sx={{ width: '48%' }}
          />
        </Stack>
      )}
    </BaseDialog>
  );
}

export default CobraCancelDialog;
